import { Link } from 'react-router-dom';
import { ArrowRight, Quote, MapPin, Users, Award, Globe, Shield, Leaf, Package, CheckCircle, Wheat, Factory, Truck, FlaskConical } from 'lucide-react';

const About = () => {
  const stats = [
    { value: '35+', label: 'Years of Basmati Expertise', icon: Award },
    { value: '40+', label: 'Countries Supplied Globally', icon: Globe },
    { value: '100%', label: 'GI-Tagged Source Regions', icon: MapPin },
    { value: '6+', label: 'International Certifications', icon: Shield }
  ];

  const sourceRegions = [
    { name: 'Punjab', description: 'The golden grain bowl of India' },
    { name: 'Haryana', description: 'Heart of basmati cultivation' },
    { name: 'Uttarakhand', description: 'Himalayan foothills purity' }
  ];

  const facilityFeatures = [
    { icon: Factory, title: 'Colour Sorters', description: 'Advanced optical sorting for perfect grain selection' },
    { icon: FlaskConical, title: 'Optical Graders', description: 'Precision grading for consistent quality' },
    { icon: Wheat, title: 'Moisture Analysers', description: 'Optimal moisture control for longevity' },
    { icon: Package, title: 'Dust-Free Packing', description: 'Hygienic packaging lines for export standards' }
  ];

  const certifications = [
    { name: 'APEDA', description: 'Agricultural Export Promotion' },
    { name: 'FSSAI', description: 'Food Safety Standards' },
    { name: 'USFDA', description: 'US Food & Drug Administration' },
    { name: 'ISO 22000', description: 'Food Safety Management' },
    { name: 'HACCP', description: 'Hazard Analysis Control' },
    { name: 'GI Tagged', description: 'Geographical Indication' },
    { name: 'EU Compliant', description: 'European Standards' }
  ];

  return (
    <main data-testid="about-page">
      {/* Hero Section */}
      <section
        data-testid="about-hero"
        className="relative min-h-[70vh] flex items-center justify-center overflow-hidden"
      >
        {/* Background - Rice Fields with Farmers */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1624547481160-d564f43324f9?w=1920')`
          }}
        />
        <div className="hero-gradient absolute inset-0" />

        {/* Embossed crest watermarks */}
        <div
          className="crest-watermark crest-watermark-center"
          style={{ backgroundImage: "url('/sawariya-logo.png')" }}
          aria-hidden="true"
        />
        <div
          className="crest-watermark crest-watermark-corner"
          style={{ backgroundImage: "url('/sawariya-logo.png')" }}
          aria-hidden="true"
        />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 pt-24 pb-12 text-center">
          <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block animate-fade-up">
            Our Legacy
          </span>
          <h1
            data-testid="about-title"
            className="font-heading text-4xl md:text-6xl font-bold text-white mb-4 animate-fade-up delay-100"
          >
            Rooted in <span className="text-gold">Heritage</span>
          </h1>
          <p className="font-heading text-2xl md:text-3xl text-white/90 mb-6 animate-fade-up delay-200">
            Trusted by the World
          </p>
          <p className="font-body text-lg text-white/70 max-w-2xl mx-auto animate-fade-up delay-300">
            Since 1989, delivering India's finest basmati rice to tables across the globe
          </p>
        </div>
      </section>

      {/* Founder's Quote Section */}
      <section data-testid="founder-quote" className="py-16 md:py-24 leaf-pattern circle-watermark relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-gold/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-navy/5 rounded-full translate-x-1/2 translate-y-1/2" />
        
        <div className="container mx-auto px-4 md:px-8 max-w-5xl relative z-10">
          <div className="text-center">
            <Quote className="w-16 h-16 text-gold/30 mx-auto mb-6" />
            <blockquote className="font-accent text-xl md:text-2xl lg:text-3xl text-navy leading-relaxed italic mb-8">
              "We did not just build a business. We built a promise — that every sack of rice leaving our doors would carry the same integrity, the same fragrance, and the same care that our founder brought to this trade over 35 years ago."
            </blockquote>
            <div className="w-24 h-0.5 bg-gold mx-auto mb-4" />
            <p className="font-body text-sm tracking-widest uppercase text-gray-500">
              The Sawariya Promise
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section data-testid="company-story" className="py-20 md:py-32 diagonal-pattern relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Content */}
            <div>
              <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
                About Sawariya
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
                A Legacy of<br />
                <span className="text-gold">Excellence</span> Since 1989
              </h2>
              <div className="w-16 h-0.5 bg-gold mb-8" />
              
              <div className="space-y-6 font-body text-lg text-gray-600 leading-relaxed">
                <p>
                  At <strong className="text-navy">Sawariya Global Exports LLP</strong>, basmati rice is not merely a commodity — it is a legacy. Founded in 1989 in the heartland of India's finest basmati-growing belt, we have spent over three and a half decades perfecting every step of the journey from paddy field to port.
                </p>
                <p>
                  What began as a deep-rooted family commitment to quality grain has grown into one of India's most trusted names in basmati processing and global export.
                </p>
              </div>
            </div>

            {/* Image with Badge */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1648102796336-0a5f2c417770?w=800"
                alt="Farmers working in rice paddy fields"
                className="w-full h-[450px] object-cover shadow-xl"
              />
              <div className="absolute -bottom-8 -left-8 bg-navy text-white p-8 shadow-xl">
                <div className="font-heading text-5xl font-bold text-gold">1989</div>
                <div className="font-body text-sm tracking-widest uppercase mt-1">Established</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-16 md:py-20 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
        }} />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                data-testid={`stat-${index}`}
                className="text-center p-6 bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300"
              >
                <stat.icon className="w-10 h-10 text-gold mx-auto mb-4" />
                <div className="font-heading text-4xl md:text-5xl font-bold text-gold mb-2">
                  {stat.value}
                </div>
                <div className="font-body text-xs md:text-sm tracking-wider uppercase text-white/80">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Source Regions */}
      <section data-testid="source-regions" className="py-20 md:py-32 wheat-pattern circle-watermark relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Map/Visual */}
            <div className="relative order-2 lg:order-1">
              <div className="bg-navy p-8 md:p-12 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
                  backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
                }} />
                
                <div className="relative z-10">
                  <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-6">
                    GI-Tagged Source Regions
                  </h3>
                  <div className="space-y-4">
                    {sourceRegions.map((region, index) => (
                      <div key={index} className="flex items-center gap-4 p-4 bg-white/10 border border-white/20">
                        <MapPin className="w-8 h-8 text-gold flex-shrink-0" />
                        <div>
                          <h4 className="font-heading text-xl text-gold font-semibold">{region.name}</h4>
                          <p className="font-body text-white/70 text-sm">{region.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="font-body text-white/60 text-sm mt-6 italic">
                    The same Himalayan foothills that have nurtured the world's finest aromatic rice for centuries
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
                Our Sourcing Philosophy
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
                Generational <span className="text-gold">Relationships</span>
              </h2>
              <div className="w-16 h-0.5 bg-gold mb-8" />
              
              <div className="space-y-6 font-body text-lg text-gray-600 leading-relaxed">
                <p>
                  We source <strong className="text-navy">exclusively</strong> from the GI-tagged basmati cultivation zones of Punjab, Haryana, and Uttarakhand — the same Himalayan foothills that have nurtured the world's finest aromatic rice for centuries.
                </p>
                <p>
                  Our relationships with farming communities are not transactional; they are <strong className="text-navy">generational</strong>. Many of the farmers who supply us today are the sons and grandsons of the farmers our founder worked with when Sawariya first opened its doors.
                </p>
                <p className="text-navy font-medium italic border-l-4 border-gold pl-6">
                  That continuity is our greatest quality assurance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Processing Facility */}
      <section data-testid="processing-facility" className="py-20 md:py-32 dot-pattern relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              State-of-the-Art Infrastructure
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
              Modern Facility, <span className="text-gold">Traditional Values</span>
            </h2>
            <div className="w-16 h-0.5 bg-gold mx-auto mb-8" />
            <p className="font-body text-lg text-gray-600 max-w-3xl mx-auto">
              Today, Sawariya Global Exports operates a state-of-the-art processing facility equipped with the latest technology — all designed to deliver rice that meets the exacting standards of supermarkets, distributors, and food service operators across Europe, the Gulf, North America, and South Asia.
            </p>
          </div>

          {/* Facility Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {facilityFeatures.map((feature, index) => (
              <div
                key={index}
                data-testid={`facility-feature-${index}`}
                className="bg-cream p-8 text-center group hover:bg-navy hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                  <feature.icon className="w-8 h-8 text-navy group-hover:text-gold transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-navy group-hover:text-white mb-3 transition-colors">
                  {feature.title}
                </h3>
                <p className="font-body text-gray-600 group-hover:text-white/80 transition-colors">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Capacity Highlight */}
          <div className="bg-navy p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
              backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
            }} />
            <div className="relative z-10">
              <p className="font-body text-white/80 text-lg mb-4">
                Our plant processes
              </p>
              <div className="font-heading text-4xl md:text-5xl font-bold text-gold mb-4">
                Thousands of Metric Tonnes
              </div>
              <p className="font-body text-white/80 text-lg">
                annually, yet our quality checks remain as personal as they were on day one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section data-testid="certifications" className="py-20 md:py-32 leaf-pattern relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              Quality Assurance
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
              International <span className="text-gold">Certifications</span>
            </h2>
            <div className="w-16 h-0.5 bg-gold mx-auto" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {certifications.map((cert, index) => (
              <div
                key={index}
                data-testid={`certification-${index}`}
                className="bg-white p-6 text-center border-2 border-transparent hover:border-gold transition-all duration-300 shadow-sm hover:shadow-lg"
              >
                <div className="w-12 h-12 mx-auto mb-4 bg-navy rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-gold" />
                </div>
                <h4 className="font-heading text-lg font-bold text-navy mb-1">
                  {cert.name}
                </h4>
                <p className="font-body text-xs text-gray-500">
                  {cert.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section data-testid="global-reach" className="py-20 md:py-32 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
        }} />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="text-center mb-12">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              Our Global Presence
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
              Trusted Across <span className="text-gold">Continents</span>
            </h2>
            <div className="w-16 h-0.5 bg-gold mx-auto" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { region: 'Europe', icon: Globe },
              { region: 'Gulf Countries', icon: Truck },
              { region: 'North America', icon: Package },
              { region: 'South Asia', icon: Users }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-white/5 border border-white/10">
                <item.icon className="w-10 h-10 text-gold mx-auto mb-4" />
                <h4 className="font-heading text-xl text-white font-semibold">{item.region}</h4>
              </div>
            ))}
          </div>

          <p className="font-body text-white/70 text-center text-lg mt-12 max-w-3xl mx-auto">
            Meeting the exacting standards of supermarkets, distributors, and food service operators worldwide.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="about-cta" className="py-20 md:py-32 wheat-pattern circle-watermark relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto mb-10">
            Join our global network of satisfied clients and experience the Sawariya difference — where heritage meets excellence in every grain.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="about-contact-btn"
              className="group inline-flex items-center justify-center gap-3 bg-navy text-white px-8 py-4 font-body text-sm tracking-widest uppercase hover:bg-navy-dark transition-colors"
            >
              Get in Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center justify-center gap-3 border-2 border-navy text-navy px-8 py-4 font-body text-sm tracking-widest uppercase hover:bg-navy hover:text-white transition-colors"
            >
              View Products
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
